import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';

/**
 * Complete a password reset. Reads the token from `?token=…` in the URL.
 * On success, forwards the user to /login with a small banner.
 */
export function ResetPasswordPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError('Use at least 12 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await api('/api/v1/auth/password/reset/confirm', {
        method: 'POST',
        body: { token, password },
        skipAuthRetry: true,
      });
      nav('/login?reset=1', { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not reset password.');
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="mx-auto mt-24 max-w-sm px-4">
        <h1 className="mb-4 text-2xl font-semibold">Missing reset link</h1>
        <p className="text-sm text-slate-500">
          Open the link in the email we sent — the URL should contain a
          reset token.
        </p>
        <p className="mt-4 text-sm text-slate-500">
          <Link to="/forgot" className="underline">Request a new one</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-24 max-w-sm px-4">
      <h1 className="mb-2 text-2xl font-semibold">Choose a new password</h1>
      <p className="mb-6 text-sm text-slate-500">
        Reset links expire 30 minutes after they're requested and can be
        used only once.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <input
          type="password" required autoComplete="new-password" minLength={12}
          placeholder="New password (12+ characters)"
          value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
        />
        <input
          type="password" required autoComplete="new-password" minLength={12}
          placeholder="Confirm password"
          value={confirm} onChange={(e) => setConfirm(e.target.value)}
          className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
        />
        {error && <p className="text-sm text-red-700">{error}</p>}
        <button
          type="submit" disabled={submitting}
          className="w-full rounded bg-slate-900 py-2 font-medium text-white disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900"
        >
          {submitting ? 'Saving…' : 'Set new password'}
        </button>
      </form>
      <p className="mt-4 text-sm text-slate-500">
        <Link to="/login" className="underline">Back to sign in</Link>
      </p>
    </div>
  );
}
